import { Grid } from '@/common/grid/Grid';
import { useDependentKey } from '@/common/hooks/key/useDependentKey';
import { StringUtils } from '@/common/utils/StringUtils';
import { alpha, useTheme } from '@mui/material';
import { useMemo, useRef } from 'react';

export const SCALES = {
  BASE: { label: '$', divisor: 1, digits: 0 },
  THOUSANDS: { label: '$K', divisor: 1000, digits: 1 },
  MILLIONS: { label: '$M', divisor: 1_000_000, digits: 1 },
};

export const DemGtnSummaryGrid = ({ data, scaleKey }) => {
  const theme = useTheme();
  const gridRef = useRef();

  // Every response brings a new set of columns, and the Grid only reads its fieldDefs on mount, so it remounts each time
  const gridKey = useDependentKey(data);

  const gridOptions = useMemo(() => {
    const divider = `1px solid ${theme.palette.divider}`;
    const totalTint = alpha(theme.palette.primary.main, 0.04);
    const ruleColor = alpha(theme.palette.text.primary, 0.5);

    // Each column mixes dollars, percents, units, and prices, so the format comes from the row
    const formatValue = ({ value, data }) => {
      if (value == null) return '';

      switch (data.format) {
        case 'MONEY': {
          const { divisor, digits } = SCALES[scaleKey];
          return StringUtils.formatNumber(value / divisor, digits, digits);
        }
        case 'PERCENT':
          return StringUtils.toPercent(value, { digits: 1 });
        case 'PRICE':
          return StringUtils.formatNumber(value, 2, 2);
        default:
          return StringUtils.formatNumber(value, 0, 0);
      }
    };

    // The Total column is tinted, except on Gross and Net Sales, which already carry a row-level tint
    const colCellStyle =
      (isTotal, isFirstInGroup) =>
      ({ data }) => {
        const style = {};
        if (isFirstInGroup) style.borderLeft = divider;
        if (isTotal && !['GROSS', 'NET'].includes(data.rowType)) style.backgroundColor = totalTint;
        return style;
      };

    const getRowStyle = ({ data }) => {
      switch (data.rowType) {
        case 'GROSS':
          return { fontWeight: 600, backgroundColor: alpha(theme.palette.primary.main, 0.07) };
        case 'DEDUCTION_GROUP':
          return { fontWeight: 500 };
        case 'DEDUCTION_TOTAL':
          return { fontWeight: 600, borderTop: `1px solid ${ruleColor}` };
        case 'NET':
          return {
            fontWeight: 700,
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
            borderBottom: `3px double ${ruleColor}`,
          };
        case 'KEY_METRIC':
          return { color: theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark };
        default:
          return null;
      }
    };

    const fieldDefs = [
      {
        field: 'label',
        displayName: '',
        pinned: 'left',
        // Summary lines always come first, even if a saved column layout says otherwise
        lockPosition: 'left',
        minWidth: 260,
        autoGrouping: true,
      },
      ...data.fieldDefs.map(({ field, displayName, children }) => {
        const isTotal = field === 'total';
        const pinned = isTotal ? 'left' : undefined;
        return {
          field,
          displayName,
          headerStyle: { borderLeft: divider },
          children: children.map((child, childIdx) => {
            const isFirstInGroup = childIdx === 0;
            const colDef = {
              field: child.field,
              displayName: child.displayName,
              headerClass: 'ag-right-aligned-header',
              pinned,
              cellStyle: colCellStyle(isTotal, isFirstInGroup),
            };
            if (isFirstInGroup) {
              colDef.headerStyle = { borderLeft: divider };
            }

            if (child.type === 'PERCENT') {
              // Percents don't change with the $/$K/$M scale, so the Grid formats them from their type
              colDef.type = 'PERCENT';
              colDef.decimalPrecision = 1;
              colDef.minWidth = 100;
            } else {
              // Grid reads a FieldDef's type as its FieldDefType, so right alignment comes from AG Grid's classes
              colDef.cellClass = 'ag-right-aligned-cell';
              colDef.minWidth = 130;
              colDef.valueFormatter = formatValue;
            }
            return colDef;
          }),
        };
      }),
    ];

    // With no rows (e.g. the filters exclude everything) the grid shows only its empty message. Tree data needs the
    // label column, so it is turned off too
    const hasRows = data.rows.length > 0;

    return {
      fieldDefs: hasRows ? fieldDefs : [],
      rowModelType: 'clientSide',
      treeData: hasRows,
      groupDefaultExpanded: -1,
      defaultColDef: { sortable: false, filter: false, resizable: true, suppressHeaderMenuButton: true },
      getRowId: ({ data }) => data.id,
      getRowStyle,
      // When the pinned columns are wider than the grid, AG Grid unpins some of them. Unpinning the Total columns rather
      // than the summary lines keeps the lines first
      processUnpinnedColumns: ({ api }) =>
        api.getDisplayedLeftColumns().filter((column) => column.getColId() !== 'label'),
      statusBar: null,
    };
  }, [data, scaleKey, theme]);

  const onGridReady = () => {
    gridRef.current.setDataSource(data.rows);
  };

  return <Grid key={gridKey} id="gtnSummaryGrid" ref={gridRef} gridOptions={gridOptions} events={{ onGridReady }} />;
};
